import { useEffect, useRef, useState } from "react";
import { entryFromHtml, runningEntry } from "../lib/buildVersion.js";

// Re-check while the tab sits open; every wake-up also checks, so the
// interval only covers a phone left face-up on the check-in table.
const CHECK_EVERY_MS = 3 * 60 * 1000;
// Wake-up events can arrive in bursts (visibilitychange + pageshow + focus);
// one network check per minute is plenty.
const MIN_GAP_MS = 60 * 1000;
// One auto-reload attempt per detected version. If we reload chasing a
// version and come back still not running it (a CDN edge serving mixed
// responses, say), looping would brick the tab — fall back to the banner.
const RELOAD_GUARD_KEY = "steelhacks-auto-reloaded-to";

// Don't yank the page out from under someone actively typing; they get the
// banner instead and the next quiet moment auto-reloads.
function userIsTyping() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
}

/**
 * Watches for a newer deploy and reloads into it. Auto-reloads at a quiet
 * moment; shows a one-tap banner instead while the user is typing, or when
 * a previous auto-reload for the same version didn't take.
 */
export default function UpdateNotice() {
  const [stale, setStale] = useState(false);
  const lastCheckRef = useRef(0);

  useEffect(() => {
    const running = runningEntry();
    if (!running) return undefined; // dev server: no hashed entry to compare

    // A finished auto-reload lands here running its target; clear the guard
    // so the next deploy can auto-reload too.
    try {
      if (sessionStorage.getItem(RELOAD_GUARD_KEY) === running) {
        sessionStorage.removeItem(RELOAD_GUARD_KEY);
      }
    } catch {
      /* storage can be unavailable; the guard just won't persist */
    }

    let cancelled = false;
    const check = async () => {
      const now = Date.now();
      if (now - lastCheckRef.current < MIN_GAP_MS) return;
      lastCheckRef.current = now;
      try {
        const res = await fetch("/", { cache: "no-store" });
        if (!res.ok) return;
        const live = entryFromHtml(await res.text());
        if (cancelled || !live || live === running) return;

        let alreadyTried = false;
        try {
          alreadyTried = sessionStorage.getItem(RELOAD_GUARD_KEY) === live;
        } catch {
          alreadyTried = true; // no storage, no loop protection: banner only
        }
        if (alreadyTried || userIsTyping()) {
          setStale(true);
          return;
        }
        try {
          sessionStorage.setItem(RELOAD_GUARD_KEY, live);
        } catch {
          /* checked above */
        }
        window.location.reload();
      } catch {
        // Offline or blocked; a later wake-up tries again.
      }
    };

    const onWake = () => {
      if (document.visibilityState === "visible") check();
    };
    // pageshow with persisted=true is the back/forward-cache resurrection —
    // the exact way iOS Safari revives a days-old scanner tab.
    const onPageShow = (event) => {
      if (event.persisted) check();
    };

    const timer = setInterval(onWake, CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onWake);
    check();
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onWake);
    };
  }, []);

  if (!stale) return null;
  return (
    <div className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom,0px)+16px)] z-[110] flex justify-center px-4 print:hidden">
      <div className="flex items-center gap-3 rounded-xl border border-cyan-400/50 bg-[#0b1230]/95 px-4 py-2.5 shadow-2xl backdrop-blur">
        <span className="text-sm text-gray-100">A newer version of this page is live.</span>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="shrink-0 rounded-lg bg-cyan-400 px-3 py-1.5 text-sm font-bold text-gray-950 hover:bg-cyan-300 transition-colors"
        >
          Reload
        </button>
      </div>
    </div>
  );
}
